import { useEffect, useRef } from "react";
import moment from "moment";
import * as am4core from "@amcharts/amcharts4/core";
import * as am4charts from "@amcharts/amcharts4/charts";
import am4themes_animated from "@amcharts/amcharts4/themes/animated";
import type { ChartSpec } from "@containers/Hermes/types";

am4core.useTheme(am4themes_animated);
am4core.addLicense("ch-custom-attribution");

type HermesChartProps = {
  spec: ChartSpec;
};

// A muted, professional categorical palette — subdued corporate tones rather
// than the loud brand hues, led by a neutral navy blue for the common
// single-series chart. Slot ORDER is the colorblind-safety mechanism (adjacent
// pairs kept maximally distinct), not cosmetic: worst adjacent CVD ΔE ≈ 44,
// well clear of the ≥12 target. Validated with the dataviz palette checker on a
// white surface; keep that order (and re-validate) if you change any hue.
const SERIES_COLORS = [
  "#2f5c9e", // navy blue
  "#1f9078", // teal
  "#cf9832", // amber
  "#6a4f9c", // violet
  "#bd5a52", // brick red
  "#2f92bb", // sky blue
  "#8a7a1a", // olive
  "#a8456f", // rose
];

// Neutral tokens shared with the rest of Hermes (Tailwind gray scale).
const TEXT_MUTED = am4core.color("#667085"); // gray-500 — axis labels and titles
const TEXT_STRONG = am4core.color("#101828"); // gray-900
const GRID = am4core.color("#eaecf0"); // gray-200
const AXIS_LINE = am4core.color("#d0d5dd"); // gray-300
const FONT_FAMILY = "Inter, sans-serif";

// Dark rounded tooltip, shared by every chart type.
function styleTooltip(tooltip: am4core.Tooltip | undefined) {
  if (!tooltip) return;
  tooltip.getFillFromObject = false;
  tooltip.background.fill = TEXT_STRONG;
  tooltip.background.fillOpacity = 0.95;
  tooltip.background.stroke = TEXT_STRONG;
  tooltip.background.cornerRadius = 8;
  tooltip.label.fill = am4core.color("#fff");
  tooltip.label.fontSize = 12;
  tooltip.label.padding(6, 10, 6, 10);
}

function styleLegend(legend: am4charts.Legend) {
  legend.position = "top";
  legend.contentAlign = "left";
  legend.fontSize = 12;
  legend.labels.template.fill = am4core.color("#475467"); // gray-600
  legend.markers.template.width = 10;
  legend.markers.template.height = 10;
  legend.paddingBottom = 8;
  disableLegendInteractivity(legend);
}

// Legends default to click-to-toggle series visibility and a pointer cursor.
// This is a static chat visualization, not an interactive dashboard widget,
// so disable that interactivity — legend items are labels only.
function disableLegendInteractivity(legend: am4charts.Legend) {
  legend.itemContainers.template.clickable = false;
  legend.itemContainers.template.focusable = false;
  legend.itemContainers.template.hoverable = false;
  legend.itemContainers.template.cursorOverStyle = am4core.MouseCursorStyle.default;
}

function applyBigNumberFormat(target: { numberFormatter: am4core.NumberFormatter | null }) {
  const formatter = new am4core.NumberFormatter();
  formatter.numberFormat = "#.##a";
  formatter.bigNumberPrefixes = [
    { number: 1e3, suffix: "K" },
    { number: 1e6, suffix: "M" },
    { number: 1e9, suffix: "B" },
    { number: 1e12, suffix: "T" },
  ];
  target.numberFormatter = formatter;
}

const MONTH_ONLY = /^\d{4}-(0[1-9]|1[0-2])$/;

// Categories that are actually dates (e.g. "2026-01-15" or "2026-01" from a
// date_histogram bucket) get reformatted for display — day-level buckets as
// "MMM DD, YYYY", month-level buckets as "MMM YYYY". Non-date categories
// (company names, countries, etc.) pass through unchanged.
function formatCategoryLabel(raw: string): string {
  if (!raw) return raw;
  if (MONTH_ONLY.test(raw)) {
    const monthly = moment(raw, "YYYY-MM", true);
    if (monthly.isValid()) return monthly.format("MMM YYYY");
  }
  const full = moment(raw, ["YYYY-MM-DD", moment.ISO_8601], true);
  if (full.isValid()) return full.format("MMM DD, YYYY");
  return raw;
}

// Reshape {categories, series} into amCharts4's row-per-category data shape:
function toChartData(spec: ChartSpec): Record<string, string | number>[] {
  return spec.categories.map((category, i) => {
    const row: Record<string, string | number> = { category: formatCategoryLabel(category) };
    for (const s of spec.series) {
      row[s.name] = s.values[i] ?? 0;
    }
    return row;
  });
}

function buildXYChart(div: HTMLDivElement, spec: ChartSpec): am4core.Sprite {
  const chart = am4core.create(div, am4charts.XYChart);
  chart.data = toChartData(spec);
  chart.fontFamily = FONT_FAMILY;
  chart.fontSize = 12;
  chart.cursor = new am4charts.XYCursor();
  chart.cursor.lineX.stroke = AXIS_LINE;
  chart.cursor.lineY.stroke = AXIS_LINE;
  if (spec.chart_type === "line") {
    // One vertical guide that follows the pointer, with every series' value
    // shown at once — the horizontal crosshair has nothing to read against.
    chart.cursor.lineX.strokeDasharray = "4,4";
    chart.cursor.lineY.disabled = true;
    chart.cursor.maxTooltipDistance = -1;
  }
  if (spec.chart_type === "bar") {
    // Column charts already highlight the hovered category via the tooltip —
    // the crosshair lines just add visual noise, no precise (x, y) point to read.
    chart.cursor.lineX.disabled = true;
    chart.cursor.lineY.disabled = true;
  }
  chart.paddingLeft = 0;
  if (chart.logo) chart.logo.disabled = true;

  const categoryAxis = chart.xAxes.push(new am4charts.CategoryAxis());
  categoryAxis.dataFields.category = "category";
  // Rotation (below) already frees up horizontal room per label, so this can
  // be lower than an unrotated axis needs — otherwise amCharts skips labels
  // it thinks won't fit, even though angled text takes far less width.
  categoryAxis.renderer.minGridDistance = 30;
  categoryAxis.renderer.labels.template.fontSize = 12;
  categoryAxis.renderer.labels.template.fill = TEXT_MUTED;
  categoryAxis.renderer.labels.template.truncate = true;
  categoryAxis.renderer.labels.template.maxWidth = 140;
  categoryAxis.renderer.line.strokeOpacity = 1;
  categoryAxis.renderer.line.stroke = AXIS_LINE;
  categoryAxis.renderer.cellStartLocation = 0.15;
  categoryAxis.renderer.cellEndLocation = 0.85;
  categoryAxis.tooltip.dy = 4;
  styleTooltip(categoryAxis.tooltip);
  // Angle labels so adjacent categories (long company names, dates) don't
  // collide into each other along the bottom.
  categoryAxis.renderer.labels.template.rotation = -45;
  categoryAxis.renderer.labels.template.horizontalCenter = "right";
  categoryAxis.renderer.labels.template.verticalCenter = "middle";
  categoryAxis.title.text = spec.x_label ?? "";
  categoryAxis.title.fontSize = 12;
  categoryAxis.title.fontWeight = "500";
  categoryAxis.title.fill = TEXT_MUTED;
  categoryAxis.title.marginTop = 8;
  // No vertical gridlines — a clean minimal look, matching the reference.
  categoryAxis.renderer.grid.template.disabled = true;

  const valueAxis = chart.yAxes.push(new am4charts.ValueAxis());
  valueAxis.min = 0;
  valueAxis.title.text = spec.y_label ?? "";
  valueAxis.title.fontSize = 12;
  valueAxis.title.fontWeight = "500";
  valueAxis.title.fill = TEXT_MUTED;
  valueAxis.renderer.labels.template.fontSize = 12;
  valueAxis.renderer.labels.template.fill = TEXT_MUTED;
  valueAxis.renderer.minGridDistance = 44;
  valueAxis.renderer.baseGrid.disabled = true;
  styleTooltip(valueAxis.tooltip);
  applyBigNumberFormat(valueAxis);
  // Subtle horizontal gridlines only.
  valueAxis.renderer.grid.template.stroke = GRID;
  valueAxis.renderer.grid.template.strokeOpacity = 1;
  valueAxis.renderer.grid.template.strokeDasharray = "4,4";
  // Floating value bubble that tracks the cursor up/down the value axis —
  // redundant with the series tooltip shown on hover over a bar. Only the
  // value (vertical) axis tooltip is disabled; the category axis label stays.
  // Line charts drop it too: with only the vertical guide there is no
  // horizontal crosshair for it to label.
  if (spec.chart_type === "bar" || spec.chart_type === "line") {
    valueAxis.cursorTooltipEnabled = false;
  }

  spec.series.forEach((s, i) => {
    const color = am4core.color(SERIES_COLORS[i % SERIES_COLORS.length]);
    const series = spec.chart_type === "line" ? chart.series.push(new am4charts.LineSeries()) : chart.series.push(new am4charts.ColumnSeries());
    series.name = s.name;
    series.dataFields.categoryX = "category";
    series.dataFields.valueY = s.name;
    series.tooltipText = `{name}: [bold]{valueY}[/]`;
    applyBigNumberFormat(series);
    // Tooltip background otherwise defaults to the series' unset `fill`
    // (LineSeries has none) instead of its stroke, so it doesn't match the
    // line color. Pin it explicitly instead of relying on getFillFromObject.
    styleTooltip(series.tooltip);

    if (series instanceof am4charts.LineSeries) {
      series.stroke = color;
      series.strokeWidth = 2.5;
      // Second and later series get a dashed stroke so overlapping lines stay
      // distinguishable by pattern, not just color (matches the reference).
      if (i > 0) {
        series.strokeDasharray = "6,4";
      }
      const bullet = series.bullets.push(new am4charts.CircleBullet());
      bullet.circle.radius = 3.5;
      bullet.circle.fill = color;
      bullet.circle.stroke = am4core.color("#fff");
      bullet.circle.strokeWidth = 2;
      bullet.circle.states.create("hover").properties.scale = 1.5;
      // A lone line gets a soft area fill so the trend reads at a glance;
      // with several series overlapping fills would just muddy each other.
      if (spec.series.length === 1) {
        series.fill = color;
        series.fillOpacity = 0.1;
        series.strokeOpacity = 1;
      }
    } else if (series instanceof am4charts.ColumnSeries) {
      series.columns.template.fill = color;
      series.columns.template.stroke = color;
      series.columns.template.column.cornerRadiusTopLeft = 6;
      series.columns.template.column.cornerRadiusTopRight = 6;
      series.columns.template.width = am4core.percent(spec.series.length > 1 ? 85 : 62);
      series.columns.template.states.create("hover").properties.fillOpacity = 0.85;
    }
  });

  if (spec.series.length > 1) {
    chart.legend = new am4charts.Legend();
    styleLegend(chart.legend);
  }

  return chart;
}

function buildPieChart(div: HTMLDivElement, spec: ChartSpec): am4core.Sprite {
  const chart = am4core.create(div, am4charts.PieChart);
  applyBigNumberFormat(chart);
  chart.fontFamily = FONT_FAMILY;
  chart.fontSize = 12;
  if (chart.logo) chart.logo.disabled = true;
  chart.innerRadius = am4core.percent(55); // donut, not solid pie

  const values = spec.series[0]?.values ?? [];
  chart.data = spec.categories.map((category, i) => ({
    category: formatCategoryLabel(category),
    value: values[i] ?? 0,
  }));

  const pieSeries = chart.series.push(new am4charts.PieSeries());
  pieSeries.dataFields.value = "value";
  pieSeries.dataFields.category = "category";
  pieSeries.slices.template.tooltipText = "{category}: [bold]{value}[/] ({value.percent.formatNumber('#.0')}%)";
  pieSeries.slices.template.stroke = am4core.color("#fff");
  pieSeries.slices.template.strokeWidth = 2;
  pieSeries.slices.template.cornerRadius = 4;
  styleTooltip(pieSeries.tooltip);
  pieSeries.labels.template.disabled = true;
  pieSeries.ticks.template.disabled = true;
  // Slices are hoverable (for the tooltip) but not clickable — no pull-out
  // effect and no pointer cursor, since this is a static chat visualization.
  pieSeries.slices.template.clickable = false;
  pieSeries.slices.template.togglable = false;
  pieSeries.slices.template.cursorOverStyle = am4core.MouseCursorStyle.default;
  pieSeries.colors.list = SERIES_COLORS.map((c) => am4core.color(c));

  chart.legend = new am4charts.Legend();
  styleLegend(chart.legend);
  chart.legend.valueLabels.template.text = "{value.percent.formatNumber('#.0')}%";
  chart.legend.valueLabels.template.fill = TEXT_MUTED;

  return chart;
}

export default function HermesChart({ spec }: HermesChartProps) {
  const divRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!divRef.current || !spec || !spec.categories?.length || !spec.series?.length) return;

    const chart = spec.chart_type === "pie" ? buildPieChart(divRef.current, spec) : buildXYChart(divRef.current, spec);

    return () => {
      chart.dispose();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [JSON.stringify(spec)]);

  if (!spec || !spec.categories?.length || !spec.series?.length) return null;

  return (
    <div className="tw-w-full tw-my-4 tw-box-border tw-p-4 tw-bg-white tw-border tw-border-solid tw-border-gray-200 tw-rounded-xl">
      {spec.title && <div className="tw-text-sm tw-leading-5 tw-font-semibold tw-text-gray-900 tw-tracking-[-0.07px] tw-mb-3">{spec.title}</div>}
      <div ref={divRef} style={{ width: "100%", height: "380px" }} />
    </div>
  );
}
